import { CurrencyType, ProductTaxType, VendorDiscountType } from './Enums';

import { VendorBasicModel, VendorStoreModel } from './VendorModel';
import { IBenefitModel, IGiviExpress, IProductInfo, IQuantifiable } from './Interfaces';
import { ShippingProviderModel } from './ShippingModel';
import { CategoryModel } from './CategoryModel';
import { CommunityVendorModel } from './CommunityModel';
import { PackageVendorModel } from './PackageModel';
import { CurrencyInfo } from '../currency-info';


export class AppProductModel {

    public constructor(init?: Partial<Pick<AppProductModel, keyof AppProductModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            Object.assign(this, init);
        }
    }

    id: number;
    sku = '';
    name = '';
    description: string;

    price = 0;
    priceWithDiscount = 0;
    points = 0;

    quantity = -1;

    allowDiscount = false;
    discountPercent = 0;

    isVisible = true;
    isActive = true;

    taxType = ProductTaxType.Exempt;
    taxPercent = 0;

    createdAt = new Date;
    updatedAt?: Date;
    deletedAt?: Date;



    get tax(): number {

        let price = this.price;
        if (this.taxType == ProductTaxType.Exempt) {
            // Round the price tax
            // price = Math.round(this.price);
            return price;

        } else if (this.taxType == ProductTaxType.IVA_01) {

            // IVA 1%
            price = price - (price / 1.01);
            return price;

        } else if (this.taxType == ProductTaxType.IVA_02) {

            // IVA 2%
            price = price - (price / 1.02);
            return price;

        }

        // Default apply IVA 13
        price = price - (price / 1.13);
        // Round the price tax
        // price = Math.round(price);
        return price;
    }
}

export class ProductModel implements IProductInfo, IQuantifiable, IGiviExpress, IBenefitModel {

    public constructor(init?: Partial<Pick<ProductModel, keyof ProductModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (init.vendor) {
                init.vendor = new VendorBasicModel(init.vendor);
            }
            if (init.categories && init.categories.length) {
                init.categories = init.categories.map(x => new CategoryModel(x));
            }
            if (init.images && init.images.length) {
                init.images = init.images.map(x => new ProductImageModel(x));
            }
            if (init.productVariants && init.productVariants.length) {
                init.productVariants = init.productVariants.map((item: any) => {
                    const pv = new ProductVariantModel(item);
                    pv.taxType = init.taxType;
                    pv.taxPercent = init.taxPercent;
                    return pv;
                });
            }
            if (init.stores && init.stores.length) {
                init.stores = init.stores.map((item: any) => new VendorStoreModel(item));
            }
            if (init.shippingProvider && typeof init.shippingProvider === 'object') {
                init.shippingProvider = new ShippingProviderModel(init.shippingProvider);
            }
            Object.assign(this, init);
        }
    }

    id: number;
    vendorId: number;
    communityId?: number;

    shippingProviderId?: number;

    sku: string;
    name = '';
    description: string;

    // Precio proveedor
    vendorPrice = 0; // Sin IVA
    vendorPriceWithTax = 0;  // vendorPriceWithDiscount * 1.13

    // Precio a Givi
    vendorDiscountType = VendorDiscountType.Custom;
    vendorPriceWithDiscount = 0; // vendorPrice / 1.15

    // Precio de venta
    price = 0;
    // Precio de venta SALE
    allowDiscount = false;
    discountPercent = 0;
    priceWithDiscount = 0;

    points = 0;

    // Product is variant
    isVariant = false;

    taxType = ProductTaxType.Exempt;
    taxPercent = 0;

    // Inventory is generic
    isGeneric = true;
    quantity = 1;
    weight = 0.1;

    isAvailable = true;
    approvedAt = new Date;

    isActive = true;
    isVisible = true;

    createdAt = new Date;
    updatedAt?: Date;
    deletedAt?: Date;

    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    isFilterCategory = false;
    isFilterProduct = false;

    categories: CategoryModel[];
    images: ProductImageModel[] = [];
    productVariants: ProductVariantModel[];
    stores: VendorStoreModel[] = [];

    vendor: VendorBasicModel;
    communityVendor: CommunityVendorModel;
    packageVendor: PackageVendorModel;

    shippingProvider?: ShippingProviderModel;


    quantitySelected = 1;
    endDateAt: Date; // Use IBenefitModel
    terms: string; // Use IBenefitModel


    set productName(v: string) {
        this.name = v;
    }
    get productName(): string {
        return this.name;
    }

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.vendor?.currencyType ?? CurrencyType.CRC);
    }


    get thumbnailUrl(): string {

        return this.images.length == 0 ? '' : this.images[0].thumbnailUrl;
    }

    // Used to display in the form invoice
    get priceTotal(): number {

        let price = (this.allowDiscount && this.priceWithDiscount > 0) ? this.priceWithDiscount : this.price;
        return price * this.quantitySelected;
    }

    // Used to display in the form invoice
    get pointsTotal(): number {

        return this.points * this.quantitySelected;
    }


    private _productVariantSelected: ProductVariantModel;

    get productVariantSelected(): ProductVariantModel {

        /* Se valida que si tiene variables y si se maneja por variables entonces se le asigna el productVariant de menor precio */
        if (this.isVariant && this.productVariants?.length) {

            if (this._productVariantSelected == null) {
                const productVariantsSort = this.productVariants.sort((a, b) => a.price - b.price);
                this._productVariantSelected = productVariantsSort[0];
            }
        }

        return this._productVariantSelected;
    }

    set productVariantSelected(value: ProductVariantModel) {

        this._productVariantSelected = value;
    }

    get availableQuantity(): number[] {

        if (this.isVariant && this.productVariants?.length) {

            if (this._productVariantSelected == null) {
                this.productVariants.sort((a, b) => a.price - b.price);
                this._productVariantSelected = this.productVariants[0];
            }
            if (!this._productVariantSelected.isGeneric) {
                return this._productVariantSelected.availableQuantity;
            }

            if (this._productVariantSelected.isGeneric && !this._productVariantSelected.isAvailable) {
                return null;
            }

            if (this._productVariantSelected.isGeneric && this._productVariantSelected.isAvailable) {

                const items = [];
                for (let i = 1; i <= 50; i++) {
                    items.push(i);
                }
                return items;
            }

        } else if (!this.isGeneric) {
            const items = [];
            for (let i = 1; i <= this.quantity; i++) {
                items.push(i);
            }
            return items;
        }

        // Default 10 (isGeneric)
        const defaultItems = [];
        for (let i = 1; i <= 10; i++) {
            defaultItems.push(i);
        }
        return defaultItems;
    }

    get isOutOfStock(): boolean {

        if (this.isGeneric && !this.isVariant && !this.isAvailable) {
            return true;
        } else if (!this.isGeneric && !this.isVariant && this.quantity <= 0) {
            return true;
        } else if (!this.isGeneric && this.isVariant && this.productVariantSelected && this.productVariantSelected.quantity <= 0) {
            return true;
        } else if (this.isGeneric && this.isVariant) {

            if (this.productVariantSelected) {
                return !this.productVariantSelected.isAvailable; // true
            } else {
                return this.productVariants.every(x => x.quantity <= 0); // true
            }
        }
        return false;
    }

    // Used to display in the form invoice
    get customerCommission(): number {
        let commission = this.customerCommissionPercent > 0 ? (this.priceTotal * (this.customerCommissionPercent / 100)) : 0;
        return commission;
    }
}

export class ProductVariantModel {

    public constructor(init?: Partial<Pick<ProductVariantModel, keyof ProductVariantModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            Object.assign(this, init);
        }
    }

    id: number;
    productId: number;
    variantId: number;
    variantOptionIds = '';
    productImageId: number;
    productImageUrl = '';
    name = '';
    price = 0;
    sku = '';
    allowDiscount = false;
    discountPercent = 0;
    isGeneric = true;
    isAvailable = true;
    quantity = 1;
    weight = 0.1;
    isVisible = true;
    createdAt: Date;
    updatedAt?: Date;

    // Must be set by product
    taxType = ProductTaxType.Exempt;
    taxPercent = 0;

    // Used by form invoice
    quantitySelected = 1;
    imagesUrlForVariant: string[] = [];

    get availableQuantity(): number[] {
        const items = [];
        for (let i = 1; i <= this.quantity; i++) {
            items.push(i);
        }
        return items;
    }

    get variantOptionIdsArray(): string[] {
        if (this.variantOptionIds && this.variantOptionIds.length) {
            return this.variantOptionIds.split('-');
        }
        return [];
    }
}

export class ProductImageModel implements IItemImage {

    public constructor(init?: Partial<Pick<ProductImageModel, keyof ProductImageModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            Object.assign(this, init);
        }
    }

    id: number;
    companyId: number;
    productId: number;

    name = '';
    url = '';
    thumbnailUrl = '';
    ordinal: number;

    createdAt: Date;
    updatedAt?: Date;

}

export class ProductSaleInfo {

    productId: number;
    productVariantId: number;
    vendorId: number;

    name: string;
    variantName: string;
    availableQuantity: number[];
    price: number;
    quantitySelected: number;
    boughtQuantity = 1;
    isGeneric = true;
    weight: number;
    sku: string;

    taxType = ProductTaxType.Exempt;
    taxPercent = 0;

    images: ProductImageModel[] = [];


    vendor: VendorBasicModel;
    points = 0;

    // Used to display in the form invoice
    get priceTotal(): number {

        return this.price * this.quantitySelected;
    }
}


export class CategoryProduct extends CategoryModel {

    public constructor(init?: Partial<Pick<CategoryProduct, keyof CategoryProduct>>) {
        super(init);

        if (init) {
            Object.assign(this, init);
        }
    }

    products: IProductInfo[];

    giftcards: IProductInfo[];
}


export interface IItemImage {

    id: number;
    url: string;
    thumbnailUrl: string;
    ordinal: number;
}
